import { useState, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  CreditCard,
  Package,
  Check,
  TrendingUp,
  Users,
  Calendar,
  AlertCircle,
  Loader2
} from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  useOrganizationSubscription,
  useAvailableModules,
  useEnabledModules,
  useEnableModule,
  useDisableModule,
  useUpdateOrganizationSubscription,
  useAvailableTiers,
} from '../api/useSubscriptions'

const categoryColors: Record<string, string> = {
  core: 'bg-blue-100 text-blue-800',
  advanced: 'bg-purple-100 text-purple-800',
  integration: 'bg-green-100 text-green-800',
  reporting: 'bg-orange-100 text-orange-800',
}

export default function SubscriptionsPage() {
  const [upgradePlanDialogOpen, setUpgradePlanDialogOpen] = useState(false)
  const [contactSalesDialogOpen, setContactSalesDialogOpen] = useState(false)
  const [contactSalesType, setContactSalesType] = useState<'enterprise' | 'on_prem'>('enterprise')
  const [isProcessingUpgrade, setIsProcessingUpgrade] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [selectedTier, setSelectedTier] = useState('Professional')
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<'monthly' | 'annual'>('monthly')

  // Fetch subscription data
  const { data: subscription, isLoading: __loadingSubscription } = useOrganizationSubscription()
  const { data: allTiers = [], isLoading: loadingTiers } = useAvailableTiers()
  const { data: availableModules = [], isLoading: loadingModules } = useAvailableModules()
  const { data: enabledModules = [], isLoading: loadingEnabled } = useEnabledModules()
  const enableModuleMutation = useEnableModule()
  const disableModuleMutation = useDisableModule()
  const updateSubscriptionMutation = useUpdateOrganizationSubscription()

  // Show all active tiers sorted by sort_order
  const standardTiers = allTiers.filter(t => t.is_active).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))

  // Create a set of enabled module IDs for easy lookup
  const enabledModuleIds = useMemo(
    () => new Set(enabledModules.filter(m => m.enabled).map(m => m.module_id)),
    [enabledModules]
  )

  // Determine billing cycle from subscription
  const billingCycle = subscription?.billing_cycle === 'annual' ? 'annual' : 'monthly'

  const handleToggleModule = async (moduleId: number, currentlyEnabled: boolean) => {
    try {
      if (currentlyEnabled) {
        await disableModuleMutation.mutateAsync(moduleId)
        toast.success('Module disabled successfully')
      } else {
        await enableModuleMutation.mutateAsync(moduleId)
        toast.success('Module enabled successfully')
      }
    } catch (error) {
      toast.error(`Failed to ${currentlyEnabled ? 'disable' : 'enable'} module. Please try again.`)
    }
  }

  const handleAddModule = async (moduleId: number) => {
    setIsProcessing(true)
    try {
      await enableModuleMutation.mutateAsync(moduleId)
      toast.success('Module added successfully')
    } catch (error) {
      toast.error('Failed to add module. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  const getModulePrice = (module: typeof availableModules[0]) => {
    const monthlyPrice = module.monthly_price_cents / 100
    const annualPrice = module.yearly_price_cents / 100
    return billingCycle === 'monthly' ? monthlyPrice : annualPrice
  }

  const calculateMonthlyTotal = () => {
    // Find the current tier to get base price
    const currentTier = allTiers.find(t =>
      t.tier.toLowerCase() === subscription?.subscription_tier.toLowerCase()
    )

    if (!currentTier) return 0

    // For Enterprise Plus and higher tiers, modules are included in base price
    // Return base tier price only
    const basePrice = billingCycle === 'monthly'
      ? (currentTier.monthly_price_cents || 0) / 100
      : ((currentTier.yearly_price_cents || 0) / 100) / 12

    return basePrice
  }

  // Show loading state
  if (loadingTiers || loadingModules || loadingEnabled) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Transform API tier data to pricing tier format
  const pricingTiers = standardTiers.map((tier, index) => ({
    name: tier.display_name || tier.tier,
    tierKey: tier.tier,
    price: {
      monthly: (tier.monthly_price_cents || 0) / 100,
      annual: (tier.yearly_price_cents || 0) / 100,
    },
    description: tier.description || '',
    features: [
      `Up to ${tier.max_users === -1 ? 'unlimited' : tier.max_users} users`,
      `Up to ${tier.max_projects === -1 ? 'unlimited' : tier.max_projects} projects`,
      tier.max_executions_per_month
        ? `${tier.max_executions_per_month === -1 ? 'Unlimited' : tier.max_executions_per_month} executions/month`
        : null,
    ].filter(Boolean) as string[],
    maxUsers: tier.max_users || 0,
    maxProjects: tier.max_projects || 0,
    highlighted: index === 1,
  }))

  const handleContactSales = (type: 'enterprise' | 'on_prem') => {
    setContactSalesType(type)
    setContactSalesDialogOpen(true)
  }

  // Show pricing page if no subscription exists
  if (!subscription) {
    return (
      <div className="p-8 space-y-8">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold">Choose Your Plan</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Select the perfect plan for your team. All plans include a 14-day free trial.
          </p>

          {/* Billing Cycle Toggle */}
          <div className="flex items-center justify-center gap-4 pt-4">
            <span className={selectedBillingCycle === 'monthly' ? 'font-semibold' : 'text-muted-foreground'}>
              Monthly
            </span>
            <Switch
              checked={selectedBillingCycle === 'annual'}
              onCheckedChange={(checked) => setSelectedBillingCycle(checked ? 'annual' : 'monthly')}
            />
            <span className={selectedBillingCycle === 'annual' ? 'font-semibold' : 'text-muted-foreground'}>
              Annual
            </span>
            {selectedBillingCycle === 'annual' && (
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                Save 17%
              </Badge>
            )}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {pricingTiers.map((tier) => (
            <Card
              key={tier.name}
              className={`relative ${tier.highlighted ? 'border-primary shadow-lg scale-105' : ''} ${
                selectedTier === tier.name ? 'ring-2 ring-primary' : ''
              }`}
            >
              {tier.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground">Most Popular</Badge>
                </div>
              )}
              <CardHeader>
                <CardTitle>{tier.name}</CardTitle>
                <CardDescription>{tier.description}</CardDescription>
                <div className="pt-4">
                  <span className="text-4xl font-bold">
                    ${selectedBillingCycle === 'monthly'
                      ? tier.price.monthly.toFixed(0)
                      : (tier.price.annual / 12).toFixed(0)}
                  </span>
                  <span className="text-muted-foreground">/mo</span>
                  {selectedBillingCycle === 'annual' && (
                    <div className="text-sm text-muted-foreground mt-1">
                      ${tier.price.annual.toFixed(0)}/year
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-600 shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full mt-6"
                  variant={tier.highlighted ? 'default' : 'outline'}
                  onClick={async () => {
                    try {
                      await updateSubscriptionMutation.mutateAsync({
                        subscription_tier: tier.tierKey,
                        billing_cycle: selectedBillingCycle,
                        max_users: tier.maxUsers,
                        max_projects: tier.maxProjects,
                      })
                      toast.success(`Successfully selected ${tier.name} plan`)
                    } catch {
                      toast.error('Failed to select plan. Please try again.')
                    }
                  }}
                  disabled={updateSubscriptionMutation.isPending}
                >
                  {updateSubscriptionMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Get Started
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Enterprise & On-Prem Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Enterprise</CardTitle>
              <CardDescription>For large organizations with advanced needs</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-600" /> Unlimited users &amp; projects
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-600" /> Priority support &amp; SLA
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-600" /> Custom integrations
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-600" /> SSO &amp; advanced security
                </li>
              </ul>
              <Button className="w-full" variant="outline" size="lg" onClick={() => handleContactSales('enterprise')}>
                Contact Sales
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>On-Premises</CardTitle>
              <CardDescription>Self-hosted for maximum control</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-600" /> Air-gapped deployment
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-600" /> Full data sovereignty
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-600" /> Custom hardware sizing
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-600" /> Dedicated support team
                </li>
              </ul>
              <Button className="w-full" variant="outline" size="lg" onClick={() => handleContactSales('on_prem')}>
                Contact Sales
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Add-on Modules Preview */}
        <Card className="max-w-6xl mx-auto">
          <CardHeader>
            <CardTitle className="text-center">Add-on Modules</CardTitle>
            <CardDescription className="text-center">
              Enhance any plan with additional capabilities
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {availableModules.slice(0, 6).map((module) => (
                <div key={module.id} className="flex items-start gap-3 p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                  <Package className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{module.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">{module.description}</div>
                    {module.monthly_price_cents > 0 && (
                      <div className="text-xs font-semibold text-primary mt-2">
                        +${(module.monthly_price_cents / 100).toFixed(2)}/mo
                      </div>
                    )}
                    <Button
                      size="sm"
                      className="mt-3 w-full"
                      onClick={() => handleAddModule(module.id)}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <Package className="mr-2 h-3 w-3" />
                          Add Module
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Contact Sales Dialog */}
        <Dialog open={contactSalesDialogOpen} onOpenChange={setContactSalesDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                Contact Sales — {contactSalesType === 'enterprise' ? 'Enterprise' : 'On-Premises'}
              </DialogTitle>
              <DialogDescription>
                {contactSalesType === 'enterprise'
                  ? 'Get a custom quote for your organization.'
                  : 'Learn about self-hosted deployment options.'}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Please contact our sales team at <strong>sales@qxproveit.com</strong> or
                  call <strong>1-800-QX-PROVE</strong> for custom pricing and deployment options.
                </AlertDescription>
              </Alert>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setContactSalesDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  const handleUpgradePlan = () => {
    if (subscription) {
      setSelectedTier(subscription.subscription_tier)
      setSelectedBillingCycle(subscription.billing_cycle === 'annual' ? 'annual' : 'monthly')
    }
    setUpgradePlanDialogOpen(true)
  }

  const handleConfirmUpgrade = async () => {
    if (!subscription) return

    const tierChanged = selectedTier.toLowerCase() !== subscription.subscription_tier.toLowerCase()
    const billingCycleChanged = selectedBillingCycle !== subscription.billing_cycle

    if (!tierChanged && !billingCycleChanged) {
      toast.info('No changes detected')
      setUpgradePlanDialogOpen(false)
      return
    }

    setIsProcessingUpgrade(true)

    try {
      const selectedTierData = pricingTiers.find(t => t.name.toLowerCase() === selectedTier.toLowerCase())

      if (!selectedTierData) {
        toast.error('Invalid tier selected')
        return
      }

      await updateSubscriptionMutation.mutateAsync({
        subscription_tier: selectedTier.toLowerCase(),
        billing_cycle: selectedBillingCycle,
        max_users: selectedTierData.maxUsers,
        max_projects: selectedTierData.maxProjects,
      })

      toast.success(`Successfully upgraded to ${selectedTier} plan (${selectedBillingCycle})`)
      setUpgradePlanDialogOpen(false)
    } catch (error) {
      toast.error('Failed to upgrade plan. Please try again or contact support.')
    } finally {
      setIsProcessingUpgrade(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Current Subscription */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Current Subscription
              </CardTitle>
              <CardDescription>Manage your subscription and billing</CardDescription>
            </div>
            <Badge className="bg-green-100 text-green-800 border-green-200">
              {subscription.status.toUpperCase()}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Plan</div>
              <div className="text-2xl font-bold capitalize">{subscription.subscription_tier}</div>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Billing Cycle</div>
              <div className="text-2xl font-bold capitalize">{subscription.billing_cycle}</div>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Next Billing Date</div>
              <div className="text-2xl font-bold">
                {new Date(subscription.current_period_end).toLocaleDateString()}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="font-medium">User Licenses</div>
                <div className="text-sm text-muted-foreground">
                  Up to {subscription.max_users} users
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Package className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="font-medium">Projects</div>
                <div className="text-sm text-muted-foreground">
                  Up to {subscription.max_projects} projects
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={handleUpgradePlan}>
              <TrendingUp className="mr-2 h-4 w-4" />
              Change Plan
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Billing Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Billing Summary
          </CardTitle>
          <CardDescription>Current billing period overview</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <div className="font-medium capitalize">{subscription.subscription_tier} Plan</div>
              <div className="text-sm text-muted-foreground">
                {enabledModules.filter(m => m.enabled).length} add-on module{enabledModules.filter(m => m.enabled).length !== 1 ? 's' : ''}
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">${calculateMonthlyTotal().toFixed(2)}</div>
              <div className="text-sm text-muted-foreground">per seat/month</div>
            </div>
          </div>
          <div className="mt-4 p-4 bg-muted/50 border rounded-lg">
            <div className="text-sm text-muted-foreground">
              Payment processing will be configured during onboarding. Contact your account
              representative for billing inquiries.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add-on Modules */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Add-on Modules
              </CardTitle>
              <CardDescription>
                Enhance your subscription with additional capabilities
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              {billingCycle === 'annual' && (
                <Badge variant="secondary" className="bg-green-100 text-green-800">
                  Save 15%
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {availableModules.map((module) => {
              const isEnabled = enabledModuleIds.has(module.id)
              const price = getModulePrice(module)
              const isModuleProcessing = enableModuleMutation.isPending || disableModuleMutation.isPending

              return (
                <Card key={module.id} className={isEnabled ? 'border-primary/50 bg-primary/5' : ''}>
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-start gap-3 flex-1">
                      <Package className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{module.name}</span>
                          {module.category && (
                            <Badge variant="outline" className={`text-xs ${categoryColors[module.category] || ''}`}>
                              {module.category}
                            </Badge>
                          )}
                          {isEnabled && (
                            <Badge className="bg-green-100 text-green-800 border-green-200">Active</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{module.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 ml-4">
                      {price > 0 && (
                        <div className="text-right">
                          <div className="font-semibold">${price.toFixed(2)}</div>
                          <div className="text-xs text-muted-foreground">
                            /{billingCycle === 'annual' ? 'year' : 'month'}
                          </div>
                        </div>
                      )}
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={() => handleToggleModule(module.id, isEnabled)}
                        disabled={isModuleProcessing}
                      />
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Upgrade Plan Dialog */}
      <Dialog open={upgradePlanDialogOpen} onOpenChange={setUpgradePlanDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Change Your Plan
            </DialogTitle>
            <DialogDescription>
              Change your subscription tier or billing cycle
            </DialogDescription>
          </DialogHeader>

          <div className="py-6 space-y-6">
            {/* Current Plan Info */}
            {subscription && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Current Plan: <strong className="capitalize">{subscription.subscription_tier}</strong> ({subscription.billing_cycle})
                </AlertDescription>
              </Alert>
            )}

            {/* Billing Cycle Toggle */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Billing Cycle</label>
              <div className="flex items-center gap-4 p-4 border rounded-lg">
                <span className={selectedBillingCycle === 'monthly' ? 'font-semibold' : 'text-muted-foreground'}>
                  Monthly
                </span>
                <Switch
                  checked={selectedBillingCycle === 'annual'}
                  onCheckedChange={(checked) => setSelectedBillingCycle(checked ? 'annual' : 'monthly')}
                />
                <span className={selectedBillingCycle === 'annual' ? 'font-semibold' : 'text-muted-foreground'}>
                  Annual
                </span>
                {selectedBillingCycle === 'annual' && (
                  <Badge variant="secondary" className="bg-green-100 text-green-800 ml-2">
                    Save 17%
                  </Badge>
                )}
              </div>
            </div>

            {/* Tier Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Plan</label>
              <div className="grid grid-cols-3 gap-4">
                {pricingTiers.map((tier) => (
                  <Card
                    key={tier.name}
                    className={`cursor-pointer transition-all ${
                      selectedTier === tier.name
                        ? 'ring-2 ring-primary border-primary'
                        : 'hover:border-primary/50'
                    }`}
                    onClick={() => setSelectedTier(tier.name)}
                  >
                    <CardContent className="p-4 text-center">
                      <div className="font-semibold">{tier.name}</div>
                      <div className="text-2xl font-bold mt-2">
                        ${selectedBillingCycle === 'monthly'
                          ? tier.price.monthly.toFixed(0)
                          : (tier.price.annual / 12).toFixed(0)}
                      </div>
                      <div className="text-xs text-muted-foreground">/month</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradePlanDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmUpgrade} disabled={isProcessingUpgrade}>
              {isProcessingUpgrade ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Confirm Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Sales Dialog */}
      <Dialog open={contactSalesDialogOpen} onOpenChange={setContactSalesDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Contact Sales — {contactSalesType === 'enterprise' ? 'Enterprise' : 'On-Premises'}
            </DialogTitle>
            <DialogDescription>
              {contactSalesType === 'enterprise'
                ? 'Get a custom quote for your organization.'
                : 'Learn about self-hosted deployment options.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Please contact our sales team at <strong>sales@qxproveit.com</strong> or
                call <strong>1-800-QX-PROVE</strong> for custom pricing and deployment options.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactSalesDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
